import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateTime } from "../../lib/api";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Eye, Pin, PinOff, GripVertical } from "lucide-react";

export const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/articles/all").then((r) => setArticles(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await api.delete(`/articles/${id}`);
      toast.success("Article supprimé");
      load();
    } catch (e) {
      toast.error("Échec de la suppression");
    }
  };

  // Unpin a featured article without opening the editor.
  const unpin = async (a) => {
    try {
      await api.put(`/articles/${a.id}`, {
        title: a.title,
        subtitle: a.subtitle || "",
        cover_image_url: a.cover_image_url || "",
        blocks: a.blocks || [],
        tags: a.tags || [],
        status: a.status,
        is_featured: false,
        author_display: a.author_display || "",
      });
      toast.success("Article dépinglé de « À la une »");
      load();
    } catch (e) {
      toast.error("Échec du dépinglage");
    }
  };

  const featuredArticles = articles
    .filter((a) => a.is_featured)
    .sort((a, b) => {
      const ao = Number.isFinite(a.featured_order) ? a.featured_order : 9999;
      const bo = Number.isFinite(b.featured_order) ? b.featured_order : 9999;
      if (ao !== bo) return ao - bo;
      return (b.updated_at || "").localeCompare(a.updated_at || "");
    });
  const FEATURED_MAX = 3;

  // Drag & drop reorder — HTML5 native, no external library. We keep an
  // in-flight local order to give instant feedback while the PUT call
  // is inflight, and reconcile with the server response on completion.
  const [dragIdx, setDragIdx] = useState(null);
  const [localOrder, setLocalOrder] = useState(null); // array of ids or null
  const displayFeatured = localOrder
    ? localOrder
        .map((id) => featuredArticles.find((a) => a.id === id))
        .filter(Boolean)
        .concat(featuredArticles.filter((a) => !localOrder.includes(a.id)))
    : featuredArticles;

  const persistOrder = async (ids) => {
    try {
      await api.put("/articles/featured/reorder", { order: ids });
      toast.success("Ordre mis à jour");
      await load();
    } catch (e) {
      toast.error("Échec de la réorganisation");
    } finally {
      setLocalOrder(null);
    }
  };

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const ids = displayFeatured.map((a) => a.id);
    const [moved] = ids.splice(dragIdx, 1);
    ids.splice(targetIdx, 0, moved);
    setLocalOrder(ids);
    setDragIdx(null);
    persistOrder(ids);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">Rédaction</div>
          <h1 className="font-display text-4xl mt-2">Articles</h1>
          <p className="text-neutral-600 text-sm mt-1">{articles.length} article{articles.length > 1 ? "s" : ""} · brouillons + publiés</p>
        </div>
        <Link to="/admin/articles/nouveau" data-testid="admin-new-article-btn" className="btn-red">
          <Plus className="h-4 w-4" /> Nouvel article
        </Link>
      </div>

      {/* Currently-pinned articles — visible reminder of what shows in
          the public homepage "À la une — Choix de la rédac" grid. Up to
          3 articles can be pinned simultaneously. */}
      <div
        data-testid="admin-featured-banner"
        className={`rounded-md border p-4 md:p-5 ${
          featuredArticles.length > 0
            ? "border-[var(--red)]/30 bg-[var(--red)]/[0.04]"
            : "border-dashed border-[var(--line-strong)] bg-[var(--bg-cream)]"
        }`}
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <span
              className={`inline-flex items-center justify-center h-9 w-9 rounded-full shrink-0 ${
                featuredArticles.length > 0
                  ? "bg-[var(--red)] text-white"
                  : "bg-white text-neutral-400 border border-[var(--line-strong)]"
              }`}
            >
              <Pin className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--red)]">
                Actuellement à la une
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">
                {featuredArticles.length} / {FEATURED_MAX} article{featuredArticles.length > 1 ? "s" : ""} épinglé{featuredArticles.length > 1 ? "s" : ""} — jusqu'à 3 s'affichent en teasers sur l'accueil (en plus de l'édito).
              </div>
            </div>
          </div>
        </div>
        {featuredArticles.length === 0 ? (
          <div className="text-sm text-neutral-500 italic pl-12">
            Aucun article épinglé — l'espace « Choix de la rédac » restera masqué sur l'accueil.
          </div>
        ) : (
          <div className="pl-12 space-y-2">
            {displayFeatured.length > 1 && (
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 -mt-1 mb-1">
                Glisser-déposer pour réorganiser l'ordre d'affichage sur l'accueil
              </div>
            )}
            {displayFeatured.map((f, idx) => (
              <div
                key={f.id}
                data-testid={`admin-featured-row-${idx}`}
                draggable={displayFeatured.length > 1}
                onDragStart={() => onDragStart(idx)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(idx)}
                className={`flex items-center gap-3 py-2 px-2 rounded transition-colors ${
                  idx >= FEATURED_MAX ? "opacity-50" : ""
                } ${
                  dragIdx === idx ? "bg-[var(--red)]/10" : "hover:bg-white/60"
                } ${displayFeatured.length > 1 ? "cursor-move" : ""}`}
              >
                {displayFeatured.length > 1 && (
                  <span
                    data-testid={`admin-featured-drag-${idx}`}
                    className="text-neutral-400 hover:text-[var(--red)] shrink-0"
                    aria-label="Poignée de glisser-déposer"
                  >
                    <GripVertical className="h-4 w-4" />
                  </span>
                )}
                <span className="text-[10px] font-mono text-neutral-400 shrink-0 w-4 text-center">
                  {idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/admin/articles/${f.id}`}
                    data-testid={`admin-featured-title-${idx}`}
                    className="font-display text-base md:text-lg leading-tight truncate block hover:underline decoration-[var(--red)] underline-offset-4"
                    title={f.title}
                  >
                    {f.title}
                  </Link>
                  {idx >= FEATURED_MAX && (
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-0.5">
                      Non affiché sur l'accueil (au-delà de la limite de {FEATURED_MAX})
                    </div>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Link
                    to={`/articles/${f.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[var(--line-strong)] text-[10px] uppercase tracking-widest hover:border-black"
                  >
                    <Eye className="h-3 w-3" /> Aperçu
                  </Link>
                  <button
                    type="button"
                    data-testid={`admin-featured-unpin-${idx}`}
                    onClick={() => unpin(f)}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[var(--red)] text-white text-[10px] uppercase tracking-widest hover:bg-black transition-colors"
                  >
                    <PinOff className="h-3 w-3" /> Dépingler
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-[var(--line)] rounded-md overflow-hidden">
        <div className="grid grid-cols-[1fr_120px_170px_120px] gap-3 px-5 py-3 text-[11px] uppercase tracking-widest text-neutral-500 border-b border-[var(--line)] bg-[var(--bg-cream)]">
          <div>Titre</div>
          <div>Statut</div>
          <div>Mis à jour</div>
          <div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="py-10 text-center text-sm text-neutral-400">Chargement…</div>
        ) : articles.length === 0 ? (
          <div className="py-16 text-center">
            <FileText className="h-6 w-6 text-neutral-300 mx-auto" />
            <div className="mt-3 font-display text-2xl">Aucun article pour l'instant</div>
            <p className="text-sm text-neutral-500 mt-1">Créez votre premier dossier.</p>
            <Link to="/admin/articles/nouveau" className="btn-red mt-6 inline-flex">
              <Plus className="h-4 w-4" /> Commencer
            </Link>
          </div>
        ) : (
          articles.map((a, i) => (
            <div
              key={a.id}
              data-testid={`article-row-${i}`}
              className="grid grid-cols-[1fr_120px_170px_120px] gap-3 px-5 py-4 items-center border-b border-[var(--line)] last:border-b-0 hover:bg-[var(--bg-cream)]"
            >
              <div className="min-w-0">
                <Link to={`/admin/articles/${a.id}`} className="font-display text-lg hover:text-[var(--red)] truncate block">
                  {a.is_featured && (
                    <Pin className="inline-block h-3.5 w-3.5 text-[var(--red)] mr-1.5 -mt-0.5" aria-label="Épinglé à la une" />
                  )}
                  {a.title}
                </Link>
                {a.subtitle && (
                  <div className="text-xs text-neutral-500 truncate mt-0.5">{a.subtitle}</div>
                )}
              </div>
              <div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${
                  a.status === "published"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-neutral-100 text-neutral-500"
                }`}>
                  {a.status === "published" ? "Publié" : "Brouillon"}
                </span>
              </div>
              <div className="text-xs text-neutral-500">{fmtDateTime(a.updated_at)}</div>
              <div className="flex justify-end gap-1.5">
                {a.status === "published" && (
                  <Link
                    to={`/articles/${a.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1.5 rounded hover:bg-neutral-100 text-neutral-600"
                    aria-label="Aperçu"
                  >
                    <Eye className="h-4 w-4" />
                  </Link>
                )}
                <button
                  onClick={() => del(a.id)}
                  data-testid={`article-delete-${i}`}
                  className="p-1.5 rounded hover:bg-red-50 text-[var(--red)]"
                  aria-label="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
